import { useState, useMemo } from 'react';
import { CalendarCheck, TrendingUp, CheckCircle2, FolderKanban, ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react';
import { useWeeklyReview } from '../hooks/useWeeklyReview';

// Simple line chart component
function TrendChart({ data, color = '#C45D3A', label, valueFormatter = (v) => v }) {
  if (data.length === 0) return null;

  const maxValue = Math.max(...data.map(d => d.value), 1);
  const minValue = Math.min(...data.map(d => d.value), 0);
  const range = maxValue - minValue || 1;

  // Calculate points for the line
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1 || 1)) * 100;
    const y = 100 - ((d.value - minValue) / range) * 80 - 10; // 10% padding top/bottom
    return `${x},${y}`;
  }).join(' ');

  const avgValue = data.reduce((sum, d) => sum + d.value, 0) / data.length;

  return (
    <div className="bg-surface-card rounded-xl border border-border p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-text-primary">{label}</h3>
        <span className="text-lg font-bold" style={{ color }}>
          {valueFormatter(avgValue)}
          <span className="text-xs text-text-tertiary ml-1">avg</span>
        </span>
      </div>
      <div className="h-24 relative">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
          {/* Grid lines */}
          <line x1="0" y1="50" x2="100" y2="50" stroke="var(--border)" strokeWidth="0.5" strokeDasharray="2,2" />

          {/* Trend line */}
          <polyline
            points={points}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data points */}
          {data.map((d, i) => {
            const x = (i / (data.length - 1 || 1)) * 100;
            const y = 100 - ((d.value - minValue) / range) * 80 - 10;
            return (
              <circle
                key={i}
                cx={x}
                cy={y}
                r="2"
                fill={color}
              />
            );
          })}
        </svg>
      </div>
      <div className="flex justify-between text-xs text-text-tertiary mt-2">
        <span>{data[0]?.week || ''}</span>
        <span>{data[data.length - 1]?.week || ''}</span>
      </div>
    </div>
  );
}

function ReviewCard({ review }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const completedDate = review.completedAt?.toDate
    ? review.completedAt.toDate()
    : new Date(review.completedAt);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-surface-card rounded-xl border border-border overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-4 p-4 text-left bg-transparent border-none cursor-pointer"
      >
        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
          <CheckCircle2 size={20} className="text-success" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-text-primary">{review.week}</p>
          <p className="text-xs text-text-tertiary">{formatDate(completedDate)}</p>
        </div>
        <div className="flex items-center gap-4 text-sm text-text-secondary">
          <span>{review.metrics?.tasksCompletedCount || 0} tasks</span>
          <span>{review.metrics?.habitsCompletionRate || 0}% habits</span>
        </div>
        <ChevronRight size={16} className={`text-text-tertiary transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border/50">
          {/* Metrics grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <div className="p-2 bg-surface-hover rounded-lg">
              <p className="text-xs text-text-tertiary">Tasks Completed</p>
              <p className="text-lg font-bold text-success">{review.metrics?.tasksCompletedCount || 0}</p>
            </div>
            <div className="p-2 bg-surface-hover rounded-lg">
              <p className="text-xs text-text-tertiary">Carried Over</p>
              <p className="text-lg font-bold text-warning">{review.metrics?.tasksCarriedOverCount || 0}</p>
            </div>
            <div className="p-2 bg-surface-hover rounded-lg">
              <p className="text-xs text-text-tertiary">Habits Rate</p>
              <p className="text-lg font-bold text-weekly">{review.metrics?.habitsCompletionRate || 0}%</p>
            </div>
            <div className="p-2 bg-surface-hover rounded-lg">
              <p className="text-xs text-text-tertiary">Projects Reviewed</p>
              <p className="text-lg font-bold text-accent">{review.metrics?.projectsReviewedCount || 0}</p>
            </div>
          </div>

          {/* Reflection */}
          {review.reflection && (review.reflection.wentWell || review.reflection.couldImprove || review.reflection.nextWeekFocus) && (
            <div className="mt-3 p-3 bg-surface-hover rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare size={14} className="text-text-tertiary" />
                <span className="text-xs font-medium text-text-secondary">Reflection</span>
              </div>
              {review.reflection.wentWell && (
                <div className="mb-2">
                  <p className="text-xs text-success mb-0.5">What went well</p>
                  <p className="text-sm text-text-secondary">{review.reflection.wentWell}</p>
                </div>
              )}
              {review.reflection.couldImprove && (
                <div className="mb-2">
                  <p className="text-xs text-warning mb-0.5">Could improve</p>
                  <p className="text-sm text-text-secondary">{review.reflection.couldImprove}</p>
                </div>
              )}
              {review.reflection.nextWeekFocus && (
                <div>
                  <p className="text-xs text-accent mb-0.5">Next week focus</p>
                  <p className="text-sm text-text-secondary">{review.reflection.nextWeekFocus}</p>
                </div>
              )}
            </div>
          )}

          {/* Duration */}
          {review.durationMinutes && (
            <p className="text-xs text-text-tertiary mt-3">
              Review took {review.durationMinutes} minutes
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function WeeklyReviewHistoryPage({ onBack }) {
  const { reviews, loading, getMetricsTrends, getAverageMetrics } = useWeeklyReview();

  // Get trend data
  const tasksTrend = useMemo(() => getMetricsTrends('tasksCompletedCount', 12), [getMetricsTrends]);
  const habitsTrend = useMemo(() => getMetricsTrends('habitsCompletionRate', 12), [getMetricsTrends]);
  const projectsTrend = useMemo(() => getMetricsTrends('projectsReviewedCount', 12), [getMetricsTrends]);

  const averages = useMemo(() => getAverageMetrics(4), [getAverageMetrics]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-8 h-8 rounded-lg bg-accent/20 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-surface-hover rounded-lg transition-colors border-none bg-transparent cursor-pointer"
            >
              <ChevronLeft size={20} className="text-text-secondary" />
            </button>
          )}
          <div>
            <h1 className="text-xl font-semibold text-text-primary">Review History</h1>
            <p className="text-sm text-text-tertiary">{reviews.length} weekly reviews completed</p>
          </div>
        </div>

        {reviews.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-surface-hover flex items-center justify-center mx-auto mb-4">
              <CalendarCheck size={32} className="text-text-tertiary" />
            </div>
            <p className="text-text-secondary mb-2">No reviews yet</p>
            <p className="text-sm text-text-tertiary">Complete your first weekly review to see history here.</p>
          </div>
        ) : (
          <>
            {/* Averages */}
            {averages && (
              <div className="mb-6 p-4 bg-surface-card rounded-xl border border-border">
                <h2 className="text-sm font-medium text-text-secondary mb-3">4-Week Averages</h2>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-success">{averages.tasksCompletedCount}</p>
                    <p className="text-xs text-text-tertiary">Tasks/Week</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-weekly">{averages.habitsCompletionRate}%</p>
                    <p className="text-xs text-text-tertiary">Habit Rate</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-accent">{averages.projectsReviewedCount}</p>
                    <p className="text-xs text-text-tertiary">Projects Reviewed</p>
                  </div>
                </div>
              </div>
            )}

            {/* Trend charts */}
            {reviews.length >= 3 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <TrendChart
                  data={tasksTrend}
                  color="#10B981"
                  label="Tasks Completed"
                  valueFormatter={(v) => Math.round(v)}
                />
                <TrendChart
                  data={habitsTrend}
                  color="#4A90D9"
                  label="Habits Rate"
                  valueFormatter={(v) => `${Math.round(v)}%`}
                />
                <TrendChart
                  data={projectsTrend}
                  color="#C45D3A"
                  label="Projects Reviewed"
                  valueFormatter={(v) => Math.round(v)}
                />
              </div>
            )}

            {/* Review list */}
            <div className="space-y-3">
              <h2 className="text-sm font-medium text-text-secondary">All Reviews</h2>
              {reviews.map(review => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
